import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../common/StickyPageHeader';
import {
  Plus,
  Edit2,
  Trash2,
  Tag,
  ArrowUpRight,
  ArrowDownRight,
  GripVertical,
  MoreVertical
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfig } from '../../context/ConfigContext';
import { useTheme } from '../../context/ThemeContext';
import { useCategoriesQuery, useInitCategories, useDeleteCategory, useReorderCategories } from '../../hooks/useQueries';
import ConfirmModal from '../common/ConfirmModal';
import { getIconComponent } from '../common/IconPicker';
import { CategoryPageSkeleton } from '../common/Skeleton';

export default function CategoryPage() {
  const { hasEncryptionKey } = useAuth();
  const { toast } = useToast();
  const { formatAmount } = useConfig();
  const { isModernUI } = useTheme();
  const navigate = useNavigate();

  // React Query hooks
  const { data: categories = [], isLoading } = useCategoriesQuery({
    enabled: hasEncryptionKey
  });
  const initCategoriesMutation = useInitCategories();
  const deleteCategoryMutation = useDeleteCategory();
  const reorderMutation = useReorderCategories();

  const [activeTab, setActiveTab] = useState('expense');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, category: null });
  const [menuOpen, setMenuOpen] = useState(null); // category._id of open menu

  // Drag/reorder state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [localCategories, setLocalCategories] = useState([]);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [draggedItem, setDraggedItem] = useState(null);
  const containerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const autoScrollRef = useRef(null);
  const autoScrollSpeedRef = useRef(0);

  // Initialize default categories on first load (for new users or to add missing defaults)
  useEffect(() => {
    // Check if we've already tried to init defaults in this session
    const hasInitialized = sessionStorage.getItem('categoriesInitialized');
    if (hasEncryptionKey && !hasInitialized) {
      sessionStorage.setItem('categoriesInitialized', 'true');
      initCategoriesMutation.mutate();
    }
  }, [hasEncryptionKey]);

  // Sync local categories with server data
  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  // Store draggedIndex in ref for non-passive event listener
  const draggedIndexRef = useRef(null);
  useEffect(() => {
    draggedIndexRef.current = draggedIndex;
  }, [draggedIndex]);

  // Attach non-passive touch event listener to document to prevent scrolling while dragging
  useEffect(() => {
    const handleTouchMoveNonPassive = (e) => {
      if (draggedIndexRef.current !== null) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMoveNonPassive);
    };
  }, []);

  const incomeCategories = localCategories.filter(c => c.type === 'income');
  const expenseCategories = localCategories.filter(c => c.type === 'expense');
  const currentCategories = activeTab === 'income' ? incomeCategories : expenseCategories;


  const handleEdit = (category) => {
    navigate(`/categories/${category._id}/edit`);
  };

  const handleDeleteClick = (category) => {
    setDeleteConfirm({ open: true, category });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.category) {
      try {
        await deleteCategoryMutation.mutateAsync(deleteConfirm.category._id);
        toast.success(`"${deleteConfirm.category.name}" deleted successfully`);
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Failed to delete category');
      }
    }
    setDeleteConfirm({ open: false, category: null });
  };

  const handleAddNew = () => {
    navigate('/categories/new');
  };


  // Mobile drag handlers with long press
  const handleTouchStart = useCallback((index, category, e) => {
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

    longPressTimerRef.current = setTimeout(() => {
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.setAttribute('data-drag-in-progress', 'true');

      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      draggedIndexRef.current = index;
      setDraggedIndex(index);
      setDraggedItem(category);
      setDragPosition({ x: touch.clientX, y: touch.clientY });
    }, 200);
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];

    if (draggedIndex === null) {
      const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
      if (dx > 10 || dy > 10) {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
      return;
    }

    setDragPosition({ x: touch.clientX, y: touch.clientY });

    // Auto-scroll when dragging near viewport edges
    const edgeThreshold = 80;
    const touchY = touch.clientY;
    const distFromTop = touchY;
    const distFromBottom = window.innerHeight - touchY;

    if (distFromTop < edgeThreshold) {
      autoScrollSpeedRef.current = -Math.round(2 + 6 * (1 - distFromTop / edgeThreshold));
    } else if (distFromBottom < edgeThreshold) {
      autoScrollSpeedRef.current = Math.round(2 + 6 * (1 - distFromBottom / edgeThreshold));
    } else {
      autoScrollSpeedRef.current = 0;
    }

    // Start rAF loop if speed is non-zero and not already running
    if (autoScrollSpeedRef.current !== 0 && !autoScrollRef.current) {
      const step = () => {
        if (autoScrollSpeedRef.current === 0) {
          autoScrollRef.current = null;
          return;
        }
        window.scrollBy(0, autoScrollSpeedRef.current);
        autoScrollRef.current = requestAnimationFrame(step);
      };
      autoScrollRef.current = requestAnimationFrame(step);
    }

    // Find which item we're over
    const items = containerRef.current?.querySelectorAll('[data-category-item]');
    if (!items) return;

    let newOverIndex = null;
    items.forEach((item, idx) => {
      const rect = item.getBoundingClientRect();
      if (touch.clientY > rect.top && touch.clientY < rect.bottom) {
        newOverIndex = idx;
      }
    });

    if (newOverIndex !== null && newOverIndex !== dragOverIndex) {
      setDragOverIndex(newOverIndex);
    }
  }, [draggedIndex, dragOverIndex]);

  // Store dragOverIndex in ref for handleTouchEnd
  const dragOverIndexRef = useRef(null);
  useEffect(() => {
    dragOverIndexRef.current = dragOverIndex;
  }, [dragOverIndex]);

  const currentCategoriesRef = useRef(currentCategories);
  useEffect(() => {
    currentCategoriesRef.current = currentCategories;
  }, [currentCategories]);

  const handleTouchEnd = useCallback(async () => {
    autoScrollSpeedRef.current = 0;
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.overscrollBehavior = '';
    document.documentElement.style.overscrollBehavior = '';
    document.body.removeAttribute('data-drag-in-progress');

    const currentDraggedIndex = draggedIndexRef.current;
    const currentDragOverIndex = dragOverIndexRef.current;

    draggedIndexRef.current = null;

    if (currentDraggedIndex === null || currentDragOverIndex === null || currentDraggedIndex === currentDragOverIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDraggedItem(null);
      return;
    }

    const cats = currentCategoriesRef.current;
    const newOrder = [...cats];
    const [removed] = newOrder.splice(currentDraggedIndex, 1);
    newOrder.splice(currentDragOverIndex, 0, removed);

    const otherCategories = localCategories.filter(c => c.type !== activeTab);
    setLocalCategories([...otherCategories, ...newOrder]);

    setDraggedIndex(null);
    setDragOverIndex(null);
    setDraggedItem(null);

    try {
      await reorderMutation.mutateAsync({
        categoryIds: newOrder.map(c => c._id),
        type: activeTab
      });
    } catch (error) {
      toast.error('Failed to reorder categories');
      setLocalCategories(categories);
    }
  }, [activeTab, localCategories, categories, reorderMutation, toast]);

  const handleDragEnd = useCallback(async () => {
    if (draggedIndex === null || dragOverIndex === null || draggedIndex === dragOverIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder local state immediately for responsive UI
    const newOrder = [...currentCategories];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dragOverIndex, 0, removed);

    // Update local state
    const otherCategories = localCategories.filter(c => c.type !== activeTab);
    setLocalCategories([...otherCategories, ...newOrder]);

    // Send to server
    try {
      await reorderMutation.mutateAsync({
        categoryIds: newOrder.map(c => c._id),
        type: activeTab
      });
    } catch (error) {
      toast.error('Failed to reorder categories');
      // Revert on error
      setLocalCategories(categories);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, dragOverIndex, currentCategories, activeTab, localCategories, categories, reorderMutation, toast]);

  // Desktop drag handlers
  const handleDesktopDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDesktopDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDesktopDrop = async (e) => {
    e.preventDefault();
    await handleDragEnd();
  };

  // Show skeleton on initial load (no cached data)
  if (isLoading && categories.length === 0) {
    return <CategoryPageSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Mobile Header */}
      <StickyPageHeader className={isModernUI ? "xl:hidden mb-2" : "xl:hidden mb-3"}>
        <h1 className={isModernUI ? "text-base font-bold text-surface-900" : "text-lg font-semibold text-surface-900"}>Categories</h1>
        <p className={isModernUI ? "text-[10px] text-surface-500" : "text-xs text-surface-500"}>Hold & drag to reorder • Tap ⋮ for options</p>
      </StickyPageHeader>

      {/* Mobile Tabs (Expense first) */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 p-1.5">
        <div className="flex gap-1">
          <button
            onClick={() => { setActiveTab('expense'); setMenuOpen(null); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'expense'
                ? 'bg-expense-100 text-expense-700'
                : 'text-surface-600 hover:bg-surface-50'
            }`}
          >
            <ArrowDownRight className="w-4 h-4" />
            <span className="text-sm">Expense ({expenseCategories.length})</span>
          </button>
          <button
            onClick={() => { setActiveTab('income'); setMenuOpen(null); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'income'
                ? 'bg-income-100 text-income-700'
                : 'text-surface-600 hover:bg-surface-50'
            }`}
          >
            <ArrowUpRight className="w-4 h-4" />
            <span className="text-sm">Income ({incomeCategories.length})</span>
          </button>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Categories</h1>
          <p className="page-subtitle">
            Drag to reorder • Order is used when adding transactions
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Category
        </button>
      </div>

      {/* Desktop Tabs (Expense first) */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-1.5">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('expense')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'expense'
                ? 'bg-expense-100 text-expense-700'
                : 'text-surface-600 hover:bg-surface-50'
            }`}
          >
            <ArrowDownRight className="w-5 h-5" />
            <span>Expense ({expenseCategories.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'income'
                ? 'bg-income-100 text-income-700'
                : 'text-surface-600 hover:bg-surface-50'
            }`}
          >
            <ArrowUpRight className="w-5 h-5" />
            <span>Income ({incomeCategories.length})</span>
          </button>
        </div>
      </div>

      {/* Empty State - only show if not loading */}
      {!isLoading && currentCategories.length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">
            No {activeTab} categories
          </h3>
          <p className="text-surface-500 mb-4">
            Add your first {activeTab} category to organize your transactions
          </p>
          <button
            onClick={handleAddNew}
            className={activeTab === 'income' ? 'btn-income inline-flex' : 'btn-expense inline-flex'}
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="table-header border-b border-surface-200">
                  <th className="table-cell w-12"></th>
                  <th className="table-cell">Category Name</th>
                  {activeTab === 'expense' && (
                    <th className="table-cell w-40">Budget</th>
                  )}
                  <th className="table-cell w-32">Type</th>
                  <th className="table-cell text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {currentCategories.map((category, index) => {
                  const Icon = getIconComponent(category.icon);
                  const color = category.color || '#6366f1';
                  const isDragging = draggedIndex === index;
                  const isDragOver = dragOverIndex === index && draggedIndex !== index;
                  return (
                    <tr
                      key={category._id}
                      data-category-item
                      draggable
                      onDragStart={(e) => handleDesktopDragStart(e, index)}
                      onDragOver={(e) => handleDesktopDragOver(e, index)}
                      onDrop={(e) => handleDesktopDrop(e)}
                      onDragEnd={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                      className={`transition-colors cursor-move ${
                        isDragging ? 'opacity-50 bg-surface-100' : 'hover:bg-surface-50'
                      } ${isDragOver ? 'border-t-2 border-t-primary-500' : ''}`}
                    >
                      <td className="table-cell">
                        <GripVertical className="w-4 h-4 text-surface-300" />
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: `${color}15` }}
                          >
                            <Icon
                              className="w-5 h-5"
                              style={{ color }}
                              fill={color}
                              fillOpacity={0.15}
                            />
                          </div>
                          <span className="font-medium text-surface-900">
                            {category.name}
                          </span>
                        </div>
                      </td>
                      {activeTab === 'expense' && (
                        <td className="table-cell">
                          {category.budget ? (
                            <span className="text-sm text-surface-900">
                              {formatAmount(category.budget)}
                              <span className="text-surface-400 ml-1">
                                /{category.budgetPeriod === 'monthly' ? 'mo' : category.budgetPeriod === 'quarterly' ? 'qtr' : 'yr'}
                              </span>
                            </span>
                          ) : (
                            <span className="text-sm text-surface-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell">
                        <span
                          className={
                            category.type === 'income'
                              ? 'badge-income'
                              : 'badge-expense'
                          }
                        >
                          {category.type}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div
            ref={containerRef}
            className="xl:hidden space-y-2 pb-20"
            onClick={() => setMenuOpen(null)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onContextMenu={(e) => e.preventDefault()}
          >
            {currentCategories.map((category, index) => {
              const Icon = getIconComponent(category.icon);
              const color = category.color || '#6366f1';
              const isDragging = draggedIndex === index;
              const isDragOver = dragOverIndex === index && draggedIndex !== index;
              const isMenuOpen = menuOpen === category._id;
              return (
                <div
                  key={category._id}
                  data-category-item
                  onTouchStart={(e) => handleTouchStart(index, category, e)}
                  className={`relative bg-white rounded-2xl p-3.5 flex items-center gap-3 border transition-all ${
                    isDragging
                      ? 'opacity-30 border-primary-300 bg-primary-50'
                      : isDragOver
                        ? 'border-primary-500 border-2 bg-primary-50/50'
                        : isMenuOpen
                          ? 'border-primary-300 bg-primary-50/30'
                          : 'border-surface-100'
                  }`}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <Icon className="w-6 h-6" style={{ color }} fill={color} fillOpacity={0.15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-surface-900 text-sm truncate">
                      {category.name}
                    </p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      {category.type === 'expense'
                        ? category.budget
                          ? `${formatAmount(category.budget)}/${category.budgetPeriod === 'monthly' ? 'mo' : category.budgetPeriod === 'quarterly' ? 'qtr' : 'yr'}`
                          : 'No budget'
                        : 'Income'
                      }
                    </p>
                  </div>
                  {/* 3-dot menu */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(isMenuOpen ? null : category._id); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-surface-400 hover:bg-surface-100 transition-colors flex-shrink-0"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {/* Dropdown menu */}
                  {isMenuOpen && (
                    <div className="absolute right-3 top-14 z-20 bg-white rounded-xl border border-surface-200 shadow-lg py-1.5 min-w-[160px]">
                      <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(null); handleEdit(category); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-surface-400" />
                        Edit Category
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(null); handleDeleteClick(category); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-expense-600 hover:bg-expense-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Floating Drag Preview for Mobile */}
          {draggedItem && draggedIndex !== null && (
            <div
              className="xl:hidden fixed pointer-events-none z-50"
              style={{
                left: Math.max(8, Math.min(dragPosition.x - 150, window.innerWidth - 308)),
                top: dragPosition.y - 30,
                width: 'calc(100vw - 16px)',
                maxWidth: '300px'
              }}
            >
              <div className="bg-white rounded-xl p-3 flex items-center gap-2 border-2 border-primary-500 shadow-2xl transform scale-105">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: `${draggedItem.color || '#6366f1'}15` }}
                >
                  {(() => {
                    const DragIcon = getIconComponent(draggedItem.icon);
                    const iconColor = draggedItem.color || '#6366f1';
                    return <DragIcon className="w-5 h-5" style={{ color: iconColor }} fill={iconColor} fillOpacity={0.15} />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-surface-900 text-sm truncate">
                    {draggedItem.name}
                  </p>
                  <p className="text-xs text-primary-500">
                    Moving...
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* FAB for Mobile */}
      <button
        onClick={handleAddNew}
        className="fab"
        style={{ opacity: 0.85 }}
        aria-label="Add category"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, category: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message={`Are you sure you want to delete "${deleteConfirm.category?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
